import React, { useReducer } from 'react'
import { NgsoRoute, useRouter, homeRoute } from '@src/routes'
import DefaultLayout, {
  LayoutSpacingOptions,
} from '@src/components/DefaultLayout'
import Breadcrumb from '@src/components/Breadcrumb'
import Container from '@material-ui/core/Container'
import { makeStyles } from '@material-ui/core/styles'
import { Theme, Grid } from '@material-ui/core'
import PageHeader from '@src/routes/Configurators/components/PageHeader'
import { FormattedMessage } from 'react-intl'
import messages from '@configurator-messages'
import { default as UUID } from 'uuid'
import { useUserSession } from '@utils/useUserSession'
import { TubeProperties } from '../../utils/enum'
import { BreadcrumbNavigationUrls } from '@src/routes/Configurators/utils/BreadcrumbNavigationUrlsEnum'
import { initialState, reducer, ContextProps } from './NgsoReducer'
import { ActiveLinkType, Products, ProductType } from './enum'
import { useCurrentUserQuery } from '@src/queries/CurrentUserQuery.generated'
import {
  useGetProductInformationQuery,
  usePlatesSessionDataQuery,
} from '@src/queries/ConfiguratorTubesQuery.generated'
import ConfiguratorPageError from '@src/routes/Configurators/utils/ConfiguratorPageError'
import Step1 from './Step1'
import Step2 from './Step2'
import LeftNavigation from './LeftNavigation'
import { sessionStorage } from 'react-storage'
import { ProductInformation } from '../ConfiguratorSharedTypes'
import HeaderHelpSection from '@src/routes/Configurators/components/HeaderHelpSection'
export interface ProductDetails {
  productType: string
  type: string
  participantId: string
  countryCode: string
  isRegUser: boolean
  locale: string
  isCSR: boolean
  isCSRSupervisor: boolean
  timeZone: string
  guestParticipantId?: string
  sapNextEnabled: boolean
  store: string
}

export interface ConfiguratorAddToMiniCart {
  productType: string
  participantId: string
  countryCode: string
  locale: string
  sapNextEnabled: boolean
  timeZone: string
  store: string
}

const validateActiveLink = (activeLink) => {
  return activeLink === '' || activeLink == null
    ? ActiveLinkType.EntryMethod
    : activeLink
}

export const configContext = React.createContext({} as ContextProps)

const useStyles = makeStyles((theme: Theme) => {
  return {
    content: {
      padding: theme.spacing(0, 9, 0, 10),
    },
    header: {
      padding: theme.spacing(0, 1, 4, 0),
    },
    pageHeader: {
      fontWeight: 900,
      fontSize: theme.typography.pxToRem(28),
    },
  }
})

const Ngso = () => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const router = useRouter()
  const classes = useStyles()
  const { userSession } = useUserSession()
  const [sessionResp, setSessionResp] = React.useState<any>({})
  const [validationMessage, setValidationMessage] = React.useState<string[]>([])
  const countryCd = userSession.country

  const { data, loading } = useCurrentUserQuery()
  const currentUser: any = data?.me || null
  const defaultEntryPath = `${NgsoRoute.index()}?product=ngsotubes&activeLink=${
    ActiveLinkType.EntryMethod
  }`

  let product = router?.query?.product ?? ''

  let loggedIn
  if (!loading)
    loggedIn =
      currentUser && currentUser.__typename === TubeProperties.LoggedInUser

  let participationId: string
  let locale: string
  let isCSR: boolean
  let isCSRSupervisor: boolean
  let isSAPNextEnabled: boolean
  let isOrderCustomer: boolean
  let isOrderableCustomer: boolean
  let isGuestShoppingCountry: boolean
  let isConfiguratorDisabled: boolean
  let accessToken = ''
  let store = ''
  const date = new Date()
  const timeZone = (date.getTimezoneOffset() / 60) * -1

  if (userSession.accessToken) {
    accessToken = userSession.accessToken
  }

  if (userSession.store) {
    store = userSession.store
  }

  if (loggedIn && currentUser) {
    participationId = currentUser.id
    locale = `${userSession.language}_${countryCd}`
    if (locale === '') {
      locale = currentUser?.metadata.locale
    }
    isCSR = currentUser?.roles.isCustomerServiceRep
    isCSRSupervisor = currentUser?.roles.isCustomProductCSR
    isSAPNextEnabled = currentUser.metadata.isSAPNextEnabled
    isGuestShoppingCountry = currentUser.metadata.guestShoppingType
    isOrderCustomer =
      currentUser?.roles.isPublicOrderingCustomer ||
      currentUser?.roles.isQuoteRequester
    if (isGuestShoppingCountry !== null) {
      isOrderableCustomer = true
    } else if (isOrderCustomer && isGuestShoppingCountry === null) {
      isOrderableCustomer = true
    } else {
      isOrderableCustomer = false
    }
    isConfiguratorDisabled =
      currentUser.participantSitePreference?.hideOligoPeptideDesign
  } else if (userSession.accessToken) {
    participationId = userSession.accessToken
    locale = `${userSession.language}_${countryCd}`
    isCSR = false
    isCSRSupervisor = false
    isSAPNextEnabled = currentUser?.metadata?.isSAPNextEnabled
    isOrderableCustomer = false
    isConfiguratorDisabled = false
  } else {
    //TODO: commented participationId as a part of fix for STRAT-11633
    //Identify why the page is going in an infinite loop when fetching the id dynamically.
    participationId = UUID.v4()
    locale = `en_${countryCd}`
    isCSR = false
    isCSRSupervisor = false
    isSAPNextEnabled = true
    isOrderableCustomer = false
    isConfiguratorDisabled = false
  }

  const productDetails: ProductDetails = {
    productType: Products.Ngso,
    type: product,
    participantId: participationId,
    countryCode: countryCd,
    isRegUser: loggedIn,
    locale: locale,
    isCSR: isCSR,
    isCSRSupervisor: isCSRSupervisor,
    timeZone: timeZone.toString(),
    sapNextEnabled: isSAPNextEnabled,
    store: store,
  }
  const productInformation: ProductInformation = {
    productType: Products.Ngso,
    countryCode: countryCd,
    isCsr: isCSR,
    isCsrSupervisor: isCSRSupervisor,
    isOrderableCustomer: isOrderableCustomer,
    regUser: loggedIn,
    locale: locale,
    sapNextEnabled: isSAPNextEnabled,
    hideOligoPeptideDesign: isConfiguratorDisabled,
  }
  const configuratorAddToMiniCart: ConfiguratorAddToMiniCart = {
    productType: product,
    countryCode: countryCd,
    participantId: participationId,
    locale: locale,
    sapNextEnabled: isSAPNextEnabled,
    timeZone: timeZone.toString(),
    store: store,
  }

  if (loggedIn) {
    const referrerParticipantId: string | null = sessionStorage.getItem(
      'CONFIGURATOR_REFERRER_ID'
    )
    if (referrerParticipantId) {
      productDetails.guestParticipantId = referrerParticipantId
    }
  }

  const { data: ngsoSessionData } = usePlatesSessionDataQuery({
    variables: {
      product: productDetails,
    },
    fetchPolicy: 'no-cache',
    skip: !accessToken,
    ssr: false,
  })

  const [productCode, setProductCode] = React.useState(0)
  const [isApiCall, setIsApiCall] = React.useState(true)
  let apiData: any = {}
  const FetchProductDetails = async (obj) => {
    const { data, loading } = useGetProductInformationQuery({
      variables: {
        input: obj,
      },
      fetchPolicy: 'no-cache',
    })
    if (!loading && loggedIn !== undefined) {
      apiData = data?.getProductInformation
    }
  }

  React.useEffect(() => {
    if (ngsoSessionData) setSessionResp(ngsoSessionData)
  }, [ngsoSessionData])

  React.useEffect(() => {
    if (Object.keys(apiData).length !== 0 && isApiCall) {
      setIsApiCall(false)
      const {
        productType,
        shippingOptions,
        brand,
        companionProducts,
        instructions,
        displayName,
        packageType,
        typeCode,
        currencyFormat,
        decimalSeparator,
        thousandSeparator,
        prefixCurrecny,
        thousandSpacing,
      } = apiData

      if (displayName === TubeProperties.UnAuthorized) {
        setProductCode(1)
      } else if (displayName.includes(TubeProperties.InvalidProduct)) {
        setProductCode(2)
      } else if (displayName.includes(TubeProperties.ProductNotValid)) {
        setProductCode(3)
      } else if (displayName.includes(TubeProperties.UserCannotOrder)) {
        setProductCode(4)
      } else {
        setProductCode(0)
        const productData = {
          productType: productType,
          shippingOptions: shippingOptions,
          brand: brand,
          companionProducts: companionProducts,
          instructions: instructions,
          packageType: packageType,
          typeCode: typeCode,
          currencyFormat: currencyFormat,
          decimalSeparator: decimalSeparator,
          thousandSeparator: thousandSeparator,
          prefixCurrecny: prefixCurrecny,
          thousandSpacing: thousandSpacing,
        }
        dispatch({
          type: 'SET_PRODUCT_INFORMATION',
          data: productData,
        })
      }
    }
    // TODO: need a test before we refactor these dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData])

  accessToken && FetchProductDetails(productInformation)

  React.useEffect(() => {
    const { activeLink } = router.query
    if (sessionResp && sessionResp?.getSessionDataForPlates) {
      if (sessionResp.getSessionDataForPlates?.data.length !== 0) {
        sessionResp.getSessionDataForPlates.data.forEach((item) => {
          state.stepNo = item.pageNo
          delete item.__typename
          item.wellList.forEach((well) => {
            delete well.__typename
            well.listError.forEach((error) => {
              delete error.__typename
            })
            well.listWarning.forEach((warning) => {
              delete warning.__typename
            })
          })
        })
        dispatch({
          type: 'UPDATE_NGSO_INPUT',
          data: sessionResp.getSessionDataForPlates.data,
        })
        dispatch({
          type: 'IS_PAGE_LOAD',
          data: true,
        })
        dispatch({ type: 'SET_STEP_NO', data: state.stepNo })
        if (
          sessionResp.getSessionDataForPlates.data[0].productType !== product
        ) {
          product = sessionResp.getSessionDataForPlates.data[0].productType
          router.push(
            `${NgsoRoute.index()}?product=${product}&activeLink=${
              activeLink ? activeLink : ActiveLinkType.EntryMethod
            }`
          )
        }
      } else {
        dispatch({
          type: 'IS_PAGE_LOAD',
          data: true,
        })
        if (
          product !== ProductType.NgsoTubes &&
          product !== ProductType.NgsoPlates
        ) {
          product = ProductType.NgsoTubes
          router.push(defaultEntryPath)
        }
      }
      sessionStorage.getItem('CONFIGURATOR_REFERRER_ID') !== null &&
        sessionStorage.removeItem('CONFIGURATOR_REFERRER_ID')
    }
  }, [sessionResp])

  React.useEffect(() => {
    return function cleanupError() {
      !loggedIn
        ? sessionStorage.setItem(
            'CONFIGURATOR_REFERRER_ID',
            productDetails.participantId
          )
        : sessionStorage.removeItem('CONFIGURATOR_REFERRER_ID')
    }
  }, [])

  if (!router || !router.query) return null
  let { activeLink = '' } = router.query
  activeLink = validateActiveLink(activeLink)

  const breadcrumbItems = [
    {
      url: homeRoute.index(),
      label: <FormattedMessage {...messages.CONF_HOME_ngso} />,
    },
    {
      url: BreadcrumbNavigationUrls.BreadcrumbCustomProducts,
      label: <FormattedMessage {...messages.CONF_CUSTOM_PRODUCTS_ngso} />,
    },
    {
      url: BreadcrumbNavigationUrls.Oligos_Probes_ngso,
      label: <FormattedMessage {...messages.CONF_OLIGOS_PROBES_ngso} />,
    },
    {
      url: '',
      label: (
        <FormattedMessage
          id="CONF_NEXT_GEN_SEQUENCING_OLIGOS_NGSO"
          defaultMessage="Next-Gen Sequencing Oligos"
        />
      ),
    },
  ]

  const sessionData = state.sessionData[0]

  const sidebarItems = [
    {
      label: <FormattedMessage {...messages.CONF_NGSO_ENTRY_METHOD} />,
      url: `${NgsoRoute.index()}?product=${product}&activeLink=${
        ActiveLinkType.EntryMethod
      }`,
      active: activeLink === ActiveLinkType.EntryMethod,
      completed: activeLink === ActiveLinkType.ConfirmOrder,
      disabled: false,
    },
    {
      label: <FormattedMessage {...messages.CONF_NGSO_CONFIRM_ORDER} />,
      url: `${NgsoRoute.index()}?product=${product}&activeLink=${
        ActiveLinkType.ConfirmOrder
      }`,
      active: activeLink === ActiveLinkType.ConfirmOrder,
      completed:
        activeLink === ActiveLinkType.EntryMethod &&
        sessionData?.pageNo === 1 &&
        !validationMessage?.filter(Boolean)?.length,
      disabled:
        (activeLink === ActiveLinkType.EntryMethod &&
          !!validationMessage?.filter(Boolean)?.length) ||
        !sessionData?.wellList?.find((item) => item.name),
    },
  ]
  const submitHandler = (messages) => {
    setValidationMessage(messages)
  }

  const ngsoContentComponent = () => {
    if (activeLink === ActiveLinkType.EntryMethod) {
      return (
        <Step1 productDetails={productDetails} submitHandler={submitHandler} />
      )
    } else if (activeLink === ActiveLinkType.ConfirmOrder) {
      return (
        <Step2
          productDetails={productDetails}
          configuratorAddToMiniCart={configuratorAddToMiniCart}
          isCSR={isCSR}
        />
      )
    } else {
      return null
    }
  }
  return (
    <configContext.Provider value={{ state, dispatch }}>
      <DefaultLayout spacing={LayoutSpacingOptions.MD}>
        {productCode !== 0 ? (
          <ConfiguratorPageError
            productCode={productCode}
            country={countryCd}
          />
        ) : (
          <Container>
            <Breadcrumb items={breadcrumbItems} />
            <Grid
              container
              direction="row"
              className={classes.header}
              alignItems="center"
            >
              <Grid item xs={5}>
                <PageHeader
                  label={
                    <div className={classes.pageHeader}>
                      <FormattedMessage
                        id="CONF_NEXT_GEN_SEQUENCING_OLIGOS_NGSO"
                        defaultMessage="Next-Gen Sequencing Oligos"
                      />
                    </div>
                  }
                />
              </Grid>
              <Grid item xs={7}>
                <HeaderHelpSection oldProduct />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={2}>
                <LeftNavigation sidebarItems={sidebarItems} />
              </Grid>
              <Grid item xs={10} className={classes.content}>
                <div>{ngsoContentComponent()}</div>
              </Grid>
            </Grid>
          </Container>
        )}
      </DefaultLayout>
    </configContext.Provider>
  )
}

export default Ngso
